import { create } from "zustand";
import { ProjectTask } from "../../../interfaces/Projects/Catalogs/projectTasks";
import { useTaskStatesStore } from "../TaskStates/TaskStatesStore";
import { TaskState } from "../../../interfaces/Projects/Catalogs/taskStates";
import { devtools } from "zustand/middleware";

interface ProjectTasksStore {
    tasksByStage: Record<number, ProjectTask[]>; // Objeto donde la clave es el stage_id y el valor es un array de tasks
    isTaskCompleted: Record<number, boolean>; // Mapa de task_id => boolean (true si está completada)
    setTasksForStage: (stageId: number, tasks: ProjectTask[]) => void;
    addTaskToStage: (stageId: number, task: ProjectTask) => void;
    updateTaskInStage: (stageId: number, task: ProjectTask) => void;
    deleteTaskFromStage: (stageId: number, taskId: number) => void;
    getTasksByStageId: (stageId: number) => ProjectTask[];
    verifyAllTaskCompleted: (stageId: number, taskId: number) => boolean;
    setIsTaskCompleted: (taskId: number, completed: boolean) => void;
    checkIfTaskIsCompleted: (task: ProjectTask) => boolean;
    markTasksInStageAsIncomplete: (stageId: number) => number;
}

export const useProjectTasksStore = create<ProjectTasksStore>()(
    devtools((set, get) => ({
        tasksByStage: {}, // Inicialmente, no hay tasks para ningún stage
        isTaskCompleted: {},
        // Establecer las tasks para un stage específico
        setTasksForStage: (stageId, tasks) => {
            const existingTasks = get().tasksByStage[stageId] || [];

            // Solo calcula para tareas nuevas o modificadas
            const newTaskCompletions = tasks.reduce((acc, task) => {
                if (!task.project_task_id) return acc;

                const existingTask = existingTasks.find(t => t.project_task_id === task.project_task_id);
                if (!existingTask || existingTask.current_task_state_id !== task.current_task_state_id) {
                    acc[task.project_task_id] = get().checkIfTaskIsCompleted(task);
                }
                return acc;
            }, {} as Record<number, boolean>);
            set((state) => ({
                tasksByStage: {
                    ...state.tasksByStage,
                    [stageId]: tasks, // Asignar las tasks al stage correspondiente
                },
                isTaskCompleted: {
                    ...state.isTaskCompleted,
                    ...newTaskCompletions
                }
            }));
        },

        // Agregar una task a un stage específico
        addTaskToStage: (stageId, task) => {
            set((state) => ({
                tasksByStage: {
                    ...state.tasksByStage,
                    [stageId]: [...(state.tasksByStage[stageId] || []), task], // Agregar la task al array correspondiente al stage
                },
            }));
        },

        // Actualizar una task en un stage específico
        updateTaskInStage: (stageId, task) => {
            set((state) => ({
                tasksByStage: {
                    ...state.tasksByStage,
                    [stageId]: state.tasksByStage[stageId].map((t) =>
                        t.project_task_id === task.project_task_id ? task : t
                    ),
                },
            }));
            if (task.project_task_id && task.current_task_state_id) {
                get().setIsTaskCompleted(task.project_task_id, get().checkIfTaskIsCompleted(task))
            }

        },

        // Eliminar una task de un stage específico
        deleteTaskFromStage: (stageId, taskId) => {
            set((state) => ({
                tasksByStage: {
                    ...state.tasksByStage,
                    [stageId]: state.tasksByStage[stageId].filter(
                        (t) => t.project_task_id !== taskId
                    ),
                },
            }));
        },

        // Obtener las tasks de un stage específico
        getTasksByStageId: (stageId) => {
            return get().tasksByStage[stageId] || []; // Retornar un array vacío si no hay tasks para el stage
        },

        verifyAllTaskCompleted: (stageId, taskId) => {
            const { doneTaskState } = useTaskStatesStore.getState();

            const tasks = get().getTasksByStageId(stageId);
            const allDone = tasks.every(
                (t) =>
                    t.project_task_id === taskId ||
                    t.current_task_state_id === doneTaskState?.task_state_id
            );

            if (!allDone) {
                return false
            } else {
                return true
            }
        },

        setIsTaskCompleted: (taskId, completed) => {
            set((state) => ({
                isTaskCompleted: {
                    ...state.isTaskCompleted,
                    [taskId]: completed,
                }
            }))
        },

        checkIfTaskIsCompleted: (task) => {
            const { taskStates } = useTaskStatesStore.getState();
            const currentState = taskStates.find(
                (state: TaskState) => state.task_state_id === task.current_task_state_id
            );
            return currentState ? currentState.task_state_name === "DONE" : false;
        },

        markTasksInStageAsIncomplete: (stageId) => {
            const tasks = get().getTasksByStageId(stageId);
            tasks.forEach((task: ProjectTask) => {
                if (task.project_task_id) {
                    get().setIsTaskCompleted(task.project_task_id, false)
                }
            });
        },
    }))
);